import { ReactNode } from 'react';
import { Radio } from 'informed';
import { useReduxState } from 'hooks';

interface Props {
  children: ReactNode;
  disabled?: boolean;
  field: string;
  id: string;
  value?: any;
}

const RadioButtonCard = ({ children, disabled, field, id, value }: Props) => {
  const { theme } = useReduxState();

  let color = 'rgba(0, 0, 0, 0.1)';
  let iconColor = theme.successTextColor;
  let activeColor = theme.successColor;
  let outlineColor = 'rgba(0, 0, 0, 0.15)';
  let labelColor = theme.textColorLight;

  return (
    <div className="radio-button">
      <Radio disabled={disabled} field={field} id={id} value={value} />
      <div className="label">
        <label htmlFor={id}>
          <div className="inner">{children}</div>
        </label>
      </div>

      <style jsx>{`
        .radio-button > :global(input) {
          opacity: 0;
          overflow: hidden;
          position: absolute;
          height: 0.1px;
          width: 0.1px;
          z-index: -1;
        }
        .radio-button > :global(input + .label label) {
          border: 2px dashed ${theme.lightGreyDark};
          border-radius: 7px;
          color: ${labelColor};
          cursor: pointer;
          display: block;
          font-size: 1.5rem;
          font-weight: 400;
          padding: 10px 40px 10px 10px;
          position: relative;
          white-space: normal;
          width: auto;
          word-wrap: break-word;
        }
        .radio-button > :global(input + .label label::before) {
          background-color: ${color};
          border: 0;
          border-radius: 10px;
          box-shadow: none;
          content: '';
          display: inline-block;
          left: auto;
          height: 18px;
          padding: 0;
          position: absolute;
          right: 16px;
          top: calc(50% - 9px);
          width: 18px;
          z-index: 1;
        }
        .radio-button > :global(input + .label label::after) {
          background-color: transparent;
          border-radius: 5px;
          content: '';
          display: inline-block;
          height: 6px;
          left: auto;
          position: absolute;
          right: 22px;
          top: calc(50% - 3px);
          transition: background-color 0.1s;
          width: 6px;
          z-index: 1;
        }
        .radio-button > :global(input + .label label:hover) {
          border: 2px dashed ${theme.lightGreyDarkActive};
        }
        .radio-button > :global(input + .label label:hover::after) {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .radio-button > :global(input:checked + .label label) {
          border: 2px solid ${theme.textColorLighter};
        }
        .radio-button > :global(input:checked + .label label::before) {
          background-color: ${activeColor};
        }
        .radio-button > :global(input:checked + .label label::after) {
          background-color: ${iconColor};
        }
        .radio-button > :global(input:disabled + .label label) {
          cursor: default;
          opacity: 0.5;
        }
        .radio-button > :global(input:disabled + .label label:hover::before) {
          color: ${color};
        }
        .radio-button > :global(input:disabled + .label label:hover::after) {
          background-color: transparent;
        }
        .radio-button
          > :global(input:disabled:checked + .label label:hover::before) {
          color: ${activeColor};
        }
        .radio-button
          > :global(input:disabled:checked + .label label:hover::after) {
          background-color: ${iconColor};
        }
        .radio-button > :global(input:focus + .label label::before),
        .radio-button > :global(input:focus:checked + .label label::before) {
          box-shadow: 0 0 0 5px ${outlineColor};
        }
        .inner {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default RadioButtonCard;
